/**
 * PhoneWidget component.
 *
 * Based on the (customised) TextWidget, but renders a
 * `react-phone-number-input` field: an international phone-number input with a
 * country selector. The value is stored in E.164 format (e.g. "+15551234567").
 *
 * @module components/Widgets/PhoneWidget/PhoneWidget
 */

import React from 'react';
import PropTypes from 'prop-types';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

import Icon from '@plone/volto/components/theme/Icon/Icon';
import FormFieldWrapper from '@plone/volto/components/manage/Widgets/FormFieldWrapper';

import clearSVG from '@plone/volto/icons/clear.svg';

const PhoneWidget = (props) => {
  const {
    id,
    value,
    onChange,
    onBlur,
    placeholder,
    isDisabled,
    required,
    error,
    resettable,
    defaultCountry,
  } = props;

  const onReset = () => onChange(id, null);

  return (
    <FormFieldWrapper {...props} className="text">
      <div className="date-time-widget-wrapper text-widget-wrapper phone-widget-wrapper">
        <PhoneInput
          id={`field-${id}`}
          name={id}
          international={false}
          defaultCountry={defaultCountry}
          disabled={isDisabled}
          placeholder={placeholder}
          value={value || undefined}
          onChange={(phone) => onChange(id, phone || undefined)}
          onBlur={() => onBlur && onBlur(id, value)}
          aria-required={required ? 'true' : undefined}
          aria-invalid={error?.length > 0 ? 'true' : undefined}
        />
        {resettable && value && (
          <button
            type="button"
            disabled={isDisabled}
            onClick={onReset}
            className="item ui noborder button"
            aria-label="Clear"
          >
            <Icon name={clearSVG} size="24px" className="close" />
          </button>
        )}
      </div>
    </FormFieldWrapper>
  );
};

PhoneWidget.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.arrayOf(PropTypes.string),
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  wrapped: PropTypes.bool,
  isDisabled: PropTypes.bool,
  placeholder: PropTypes.string,
  resettable: PropTypes.bool,
  // ISO 3166-1 alpha-2 country code pre-selected when the value has no
  // country calling code yet.
  defaultCountry: PropTypes.string,
};

PhoneWidget.defaultProps = {
  description: null,
  required: false,
  error: [],
  value: null,
  resettable: true,
  defaultCountry: 'US',
};

export default PhoneWidget;
